// The timesheet's words on the client (rules §3): days, wall-clock minutes and spans, in the board's
// style ("Fri Oct 23", "3 p.m.", "3:40 p.m.", "2 a.m. Sat"). The server says instants in words itself;
// these are for the minutes time.day hands over (§9.9: the browser does no zone maths).
import { addDays } from '@flowology/kernel';
import { minutesText } from '../books/timesheet-model.ts';

export { minutesText };

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const parts = (date: string) => date.split('-').map(Number) as [number, number, number];

/** "Fri Oct 23", as the board says a day (board-bp-model dayWords). */
export function dayWords(date: string): string {
  const [y, m, d] = parts(date);
  return `${WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${MO[m - 1]} ${d}`;
}
/** "Oct 23" */
export const shortDay = (date: string) => dayWords(date).slice(4);
/** "Sat" */
export const weekday = (date: string) => dayWords(date).slice(0, 3);

/** "Oct 12 – 25", or across months "Sep 28 – Oct 11". */
export function periodWords(from: string, to: string): string {
  const [, fm] = parts(from);
  const [, tm, td] = parts(to);
  return fm === tm ? `${shortDay(from)} – ${td}` : `${shortDay(from)} – ${shortDay(to)}`;
}

/** "7:12 a.m.", "3 p.m.", "12 p.m.", "12 a.m.": a wall-clock minute, the day left off. */
export function clockWord(minute: number): string {
  const m = ((Math.round(minute) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h % 12 || 12}${mm ? `:${String(mm).padStart(2, '0')}` : ''} ${h < 12 ? 'a.m.' : 'p.m.'}`;
}

/** A minute of `date`'s clock: past its midnight it names the next day, "2 a.m. Sat". */
export function clockOn(minute: number, date: string): string {
  return minute >= 1440 ? `${clockWord(minute)} ${weekday(addDays(date, 1))}` : clockWord(minute);
}

export const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Tom", "Tom and Lee", "Tom, Lee and Priya". */
export const listWords = (names: string[]) =>
  names.length < 2 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;

/** A non-job code in a sentence: "lunch", "shop time", "the supply-house run" (the server's codeWords). */
export function codeWords(code: string, name?: string | null): string {
  if (code === 'lunch') return 'lunch';
  if (code === 'shop') return 'shop time';
  if (code === 'supply_house') return 'the supply-house run';
  return name ? name.toLowerCase() : 'time off a job';
}
